import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { supabase, signIn, signUp, signOut, getCurrentUser, onAuthStateChange, getSessionSmart, createUserProfile, isNewUser } from '@/lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

// Session hardening helpers to avoid infinite loading and ensure persistence across refresh
const SUPABASE_LOCAL_REGEX = /^sb-.*-auth-token$/;

// Cache for session data to prevent excessive API calls (kept internal to prevent SSR leakage)
let sessionCache: { session: any; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Debounce utility
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

/**
 * Remove legacy/bad values written under Supabase storageKey that are not valid sessions.
 */
function sanitizeStaleSupabaseKey() {
  try {
    const L = window.localStorage;
    const S = window.sessionStorage;
    const clean = (key: string) => {
      const raw = L.getItem(key) || S.getItem(key);
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        // If it looks like our old mirror signature (no currentSession/session), remove it
        if (parsed && typeof parsed === "object" && ("updatedAt" in parsed || "userId" in parsed) && !("currentSession" in parsed) && !("session" in parsed)) {
          L.removeItem(key);
          S.removeItem(key);
        }
      } catch { }
    };
    clean("trestune.auth");
    clean("trestune.auth.mirror");
  } catch { }
}

/**
 * Get session with caching to prevent excessive API calls
 */
async function getSessionWithCache() {
  const now = Date.now();

  // Return cached session if still valid
  if (sessionCache && (now - sessionCache.timestamp) < CACHE_DURATION) {
    return sessionCache.session;
  }

  try {
    const { data } = await supabase.auth.getSession();
    const session = data.session;

    // Cache the session
    sessionCache = {
      session,
      timestamp: now
    };

    return session;
  } catch (error) {
    console.warn('Session fetch failed:', error);
    return null;
  }
}

/**
 * Attempt to restore a Supabase session from localStorage when getSession() returns null.
 * - Scans the standard sb-*-auth-token entry written by supabase-js v2
 * - If a refresh_token is found, calls supabase.auth.setSession to rehydrate the session
 * - Falls back to refreshSession() when token cache exists but no refresh_token is present
 */
async function restoreSupabaseSessionFromLocalStorage() {
  try {
    if (typeof window === 'undefined') return null;

    // Helper to try restoring from a specific storage/key
    const tryRestoreFromKey = async (storage: Storage, key: string) => {
      try {
        const raw = storage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const currentSession = parsed?.currentSession || parsed?.session || null;
        const refresh_token =
          currentSession?.refresh_token ||
          parsed?.refreshToken ||
          parsed?.refresh_token ||
          null;
        const access_token =
          currentSession?.access_token ||
          parsed?.accessToken ||
          parsed?.access_token ||
          null;

        if (refresh_token) {
          const { data, error } = await supabase.auth.setSession({
            refresh_token,
            access_token: access_token || '',
          });
          if (!error) {
            // Update cache
            sessionCache = {
              session: data?.session ?? null,
              timestamp: Date.now()
            };
            return data?.session ?? null;
          }
        }
      } catch {
        // ignore JSON parse errors
      }
      return null;
    };

    // 1) Try our custom storage key in both localStorage and sessionStorage
    const L = window.localStorage;
    const S = window.sessionStorage;
    // Prefer restoring from Supabase's actual storageKey first, then our mirror (metadata-only)
    let restored =
      (await tryRestoreFromKey(L, 'trestune.auth')) ||
      (await tryRestoreFromKey(S, 'trestune.auth')) ||
      (await tryRestoreFromKey(L, 'trestune.auth.mirror')) ||
      (await tryRestoreFromKey(S, 'trestune.auth.mirror'));
    if (restored) return restored;

    // 2) Legacy/standard Supabase key scan in localStorage
    let sawSupabaseToken = false;
    if (L) {
      for (let i = 0; i < L.length; i++) {
        const key = L.key(i) || '';
        if (!SUPABASE_LOCAL_REGEX.test(key)) continue;
        sawSupabaseToken = true;

        try {
          const raw = L.getItem(key);
          const parsed = raw ? JSON.parse(raw) : {};
          const currentSession = parsed?.currentSession || parsed?.session || null;
          const refresh_token =
            currentSession?.refresh_token ||
            parsed?.refreshToken ||
            parsed?.refresh_token ||
            null;
          const access_token =
            currentSession?.access_token ||
            parsed?.accessToken ||
            parsed?.access_token ||
            null;

          if (refresh_token) {
            const { data, error } = await supabase.auth.setSession({
              refresh_token,
              access_token: access_token || '',
            });
            if (!error) {
              // Update cache
              sessionCache = {
                session: data?.session ?? null,
                timestamp: Date.now()
              };
              return data?.session ?? null;
            }
          }
        } catch {
          // ignore JSON parse errors
        }
      }
    }

    // 3) If we saw a Supabase token, attempt a refresh (but only if we haven't tried recently)
    if (sawSupabaseToken && (!sessionCache || (Date.now() - sessionCache.timestamp) > 30000)) {
      try {
        const { data } = await supabase.auth.refreshSession();
        const session = data?.session ?? null;

        // Update cache
        sessionCache = {
          session,
          timestamp: Date.now()
        };

        return session;
      } catch {
        return null;
      }
    }

    return null;
  } catch {
    return null;
  }
}

// Simplified mirror logic - mostly just for debugging now
function saveLocalSessionMirror(session: any | null) {
  try {
    if (!session) {
      localStorage.removeItem('trestune.auth.mirror');
      return;
    }
    // Only save minimal info to avoid conflicts
    localStorage.setItem(
      'trestune.auth.mirror',
      JSON.stringify({
        updatedAt: Date.now(),
        userId: session.user?.id
      })
    );
  } catch { }
}

interface User {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  avatar_url?: string;
  cover_photo_url?: string;
  role?: string;
  bio?: string;
  location?: string;
  phone?: string;
  linkedin_url?: string;
  github_url?: string;
  twitter_url?: string;
  portfolio_url?: string;
  skills?: string[];
  interests?: string[];
  profile_type?: string;
  date_of_birth?: string;
  university?: string;
  college_email?: string;
  email_confirmed?: boolean;
}

interface AuthContextType {
  user: User | null;
  supabaseUser: SupabaseUser | null;
  session: any;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ error?: any }>;
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<{ data?: any; error?: any }>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<{ error?: any }>;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let initTimeout: ReturnType<typeof setTimeout>;

    const initializeAuth = async () => {
      try {
        logger.auth('🔄 Initializing auth...');

        // Clear stale/bad prior keys once at startup to prevent infinite spinners
        try { sanitizeStaleSupabaseKey(); } catch { }

        // Set a shorter timeout to prevent endless loading
        initTimeout = setTimeout(() => {
          if (isMounted) {
            console.warn('⚠️ Auth initialization timeout, setting loading to false');
            setIsLoading(false);
            setUser(null);
            setSession(null);
            setSupabaseUser(null);
            setAuthError('Authentication initialization timed out');
          }
        }, 2000); // Reduced to 2 seconds for faster feedback

        // Get initial session with smart caching
        let session = await getSessionSmart();

        if (!isMounted) return;

        // If no session from getSession(), try restoration
        if (!session?.user) {
          try {
            const restored = await restoreSupabaseSessionFromLocalStorage();
            if (restored?.user) {
              session = restored;
              // Session restored

            }
          } catch (error) {
            console.warn('Session restoration failed:', error);
          }
        }

        setSession(session);
        setSupabaseUser(session?.user ?? null);
        saveLocalSessionMirror(session);

        if (session?.user) {


          // Check if user profile exists
          const userIsNew = await isNewUser(session.user.id);

          // Don't auto-create profile - let users choose their profile type first
          // Profile will be created during the profile selection process


          // Fetch user profile with timeout
          try {
            const profilePromise = supabase
              .from('user_profiles')
              .select('*')
              .eq('user_id', session.user.id)
              .single();

            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Profile fetch timeout')), 3000)
            );

            const { data: profile } = await Promise.race([profilePromise, timeoutPromise]) as any;

            const userData = {
              id: session.user.id,
              name: profile?.name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
              email: session.user.email || '',
              firstName: profile?.first_name || session.user.user_metadata?.first_name || '',
              lastName: profile?.last_name || session.user.user_metadata?.last_name || '',
              profile_type: profile?.profile_type,
              date_of_birth: profile?.date_of_birth,
              university: profile?.university,
              college_email: profile?.college_email,
              bio: profile?.bio,
              location: profile?.location,
              phone: profile?.phone,
              linkedin_url: profile?.linkedin,
              github_url: profile?.github,
              portfolio_url: profile?.website,
              skills: profile?.skills,
              avatar_url: profile?.profile_photo_url,
              email_confirmed: !!session.user.email_confirmed_at,
            };

            // User data loaded

            setUser(userData);
          } catch (error) {
            console.error('Error fetching user profile:', error);
            // Fallback to basic user data
            setUser({
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
              email: session.user.email || '',
              firstName: session.user.user_metadata?.first_name || '',
              lastName: session.user.user_metadata?.last_name || '',
              email_confirmed: !!session.user.email_confirmed_at,
            });
          }
        } else {

          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setAuthError('Authentication initialization failed');
        // Ensure we don't leave the app inside a loading state if init fails
        setUser(null);
        setSession(null);
        setSupabaseUser(null);
      } finally {
        if (isMounted) {
          clearTimeout(initTimeout);
          // FORCE loading to false at the end of initialization NO MATTER WHAT
          setIsLoading(false);
          logger.auth('Auth initialization complete');
        }
      }
    };

    // Initialize auth
    initializeAuth();

    // Listen for auth changes with optimized debouncing
    const debouncedAuthChange = debounce(async (event: string, session: any) => {
      if (!isMounted) return;

      // Auth state changed
      console.log(`[Auth] Auth state change intercepted: ${event}`);

      // CRITICAL: Ignore unverified sessions to prevent premature auto-login
      // This ensures AuthRoute doesn't redirect newly registered (but unverified) users
      if (session?.user && !session.user.email_confirmed_at) {
        console.log('[Auth] Ignoring unverified session - waiting for email verification');

        // Force session to null in our state, even if Supabase has one
        // This keeps the user "logged out" in the UI (and AuthRoutes) until verified
        setSession(null);
        setSupabaseUser(null);
        setUser(null);
        return;
      }

      // Update cache
      sessionCache = {
        session,
        timestamp: Date.now()
      };

      setSession(session);
      setSupabaseUser(session?.user ?? null);
      saveLocalSessionMirror(session || null);

      if (session?.user) {
        // Check if user profile exists and create if needed
        const userIsNew = await isNewUser(session.user.id);

        // Don't auto-create profile - let users choose their profile type first
        // Profile will be created during the profile selection process


        // Fetch user profile with timeout
        try {
          const profilePromise = supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', session.user.id)
            .single();

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Profile fetch timeout')), 3000)
          );

          const { data: profile } = await Promise.race([profilePromise, timeoutPromise]) as any;

          setUser({
            id: session.user.id,
            name: profile?.name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
            email: session.user.email || '',
            firstName: profile?.first_name || session.user.user_metadata?.first_name || '',
            lastName: profile?.last_name || session.user.user_metadata?.last_name || '',
            profile_type: profile?.profile_type,
            date_of_birth: profile?.date_of_birth,
            university: profile?.university,
            college_email: profile?.college_email,
            bio: profile?.bio,
            location: profile?.location,
            phone: profile?.phone,
            linkedin_url: profile?.linkedin,
            github_url: profile?.github,
            portfolio_url: profile?.website,
            skills: profile?.skills,
            avatar_url: profile?.profile_photo_url,
            email_confirmed: !!session.user.email_confirmed_at,
          });
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Fallback to basic user data
          setUser({
            id: session.user.id,
            name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
            email: session.user.email || '',
            firstName: session.user.user_metadata?.first_name || '',
            lastName: session.user.user_metadata?.last_name || '',
            email_confirmed: !!session.user.email_confirmed_at,
          });
        }
      } else {
        setUser(null);
      }

      // Always clear loading after processing auth change
      setIsLoading(false);
    }, 300); // Increased debounce to 300ms

    const {
      data: { subscription },
    } = onAuthStateChange(debouncedAuthChange);

    // Optimized visibility change handler with caching
    const onVisibilityChange = debounce(async () => {
      if (document.visibilityState === 'visible' && !isLoading && !user) {
        try {
          // Use cached session if available
          const session = await getSessionWithCache();
          if (session?.user && !user) {
            // Only refresh if we don't have a user but there's a session
            setSession(session);
            setSupabaseUser(session.user);
            setUser({
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
              email: session.user.email || '',
              firstName: session.user.user_metadata?.first_name || '',
              lastName: session.user.user_metadata?.last_name || '',
              email_confirmed: !!session.user.email_confirmed_at,
            });
          }
        } catch (e) {
          console.warn('Session refresh on visibility failed:', e);
        }
      }
    }, 1000); // Debounce visibility changes to 1 second
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isMounted = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      subscription.unsubscribe();
    };
  }, []);





  const login = async (email: string, password: string, rememberMe: boolean = true): Promise<{ error?: any }> => {
    try {
      setIsLoading(true);

      // Set remember-me preference BEFORE calling sign in so Supabase stores tokens in the correct storage
      try {
        localStorage.setItem('trestune.rememberMe', rememberMe ? 'true' : 'false');
      } catch { }

      const { data, error } = await signIn(email, password);

      if (error) {
        setIsLoading(false);
        return { error };
      }

      // Wait for auth state change to complete with timeout
      const authTimeout = setTimeout(() => {
        console.warn('Auth state change timeout after login');
        setIsLoading(false);
      }, 3000);

      // Clear timeout when auth state updates
      const clearAuthTimeout = () => {
        clearTimeout(authTimeout);
        setIsLoading(false);
      };

      // User state will be updated automatically by the auth state change listener
      // The timeout will be cleared by the auth state change handler
      return { error: null };
    } catch (err) {
      console.error('Login failed:', err);
      setIsLoading(false);
      return { error: { message: "Authentication failed" } };
    }
  };

  const register = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ): Promise<{ data?: any; error?: any }> => {
    try {
      const fullName = `${firstName} ${lastName}`;

      // Sign up with user metadata including the name
      const { data, error } = await signUp(email, password, {
        data: {
          name: fullName,
          full_name: fullName,
          first_name: firstName,
          last_name: lastName,
        }
      });

      if (error) {
        return { error };
      }

      // CRITICAL: Force sign out after registration to enforce email verification
      // This ensures users MUST click the email link before accessing the app
      try {
        if (data?.session) {
          console.log('🛑 Registration successful. Signing out to enforce email verification flow.');
          console.log('User must click email link to verify:', email);

          // Clear the session completely
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);

          // Clear any cached auth data
          await signOut();
        }
      } catch (e) {
        console.error('Error during forced signout:', e);
      }

      return { data, error: null };
    } catch (err) {
      console.error('Registration failed:', err);
      return { error: { message: "Registration failed" } };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut();
    } catch (err) {
      console.error('Logout error:', err);
    }

    // Clear session cache
    sessionCache = null;

    // Aggressively clear persisted tokens across both storage scopes to avoid stale state
    try {
      localStorage.removeItem('trestune.auth');
    } catch { }
    try {
      sessionStorage.removeItem('trestune.auth');
    } catch { }
    try {
      localStorage.removeItem('trestune.auth.mirror');
    } catch { }
    try {
      localStorage.removeItem('trestune.rememberMe');
    } catch { }

    // Always clear local state
    setUser(null);
    setSupabaseUser(null);
    setSession(null);
  };

  const updateProfile = async (updates: Partial<User>): Promise<{ error?: any }> => {
    if (!user || !supabaseUser) {
      return { error: { message: 'No user found' } };
    }

    try {
      // Updating profile


      // Prepare the profile update for database
      const profileUpdate = {
        user_id: supabaseUser.id,
        name: updates.name || user.name,
        first_name: updates.firstName || user.firstName,
        last_name: updates.lastName || user.lastName,
        email: updates.email || user.email || supabaseUser.email,
        bio: updates.bio,
        location: updates.location,
        phone: updates.phone,
        linkedin: updates.linkedin_url,
        github: updates.github_url,
        twitter: updates.twitter_url,
        website: updates.portfolio_url,
        skills: updates.skills,
        interests: updates.interests,
        profile_photo_url: updates.avatar_url,
        cover_photo_url: updates.cover_photo_url,
        profile_type: updates.profile_type,
        date_of_birth: updates.date_of_birth,
        university: (updates as any).university,
        college_email: (updates as any).college_email,
        updated_at: new Date().toISOString()
      };

      // Remove undefined values (type-safe)
      const cleanedProfileUpdate = Object.fromEntries(
        Object.entries(profileUpdate).filter(([, v]) => v !== undefined)
      );

      // Upserting profile data


      // Update database first
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert(cleanedProfileUpdate, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Database update error:', error);
        return { error: { message: error.message || 'Database update failed' } };
      }



      // Update local user state with the returned data
      const updatedUser = {
        ...user,
        ...updates,
        // Ensure profile_type is properly set from the database response
        profile_type: data.profile_type || updates.profile_type,
      };

      setUser(updatedUser);


      return { error: null };
    } catch (err) {
      console.error('❌ Profile update failed:', err);
      return { error: { message: 'Profile update failed' } };
    }
  };

  const value = {
    user,
    supabaseUser,
    session,
    login,
    register,
    logout,
    updateProfile,
    isLoading,
    isAuthenticated: !!user,
    refreshUser: async () => {
      if (supabaseUser) {
        // Refresh user data from current session
        setUser({
          id: supabaseUser.id,
          name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
          email: supabaseUser.email || '',
          firstName: supabaseUser.user_metadata?.first_name || '',
          lastName: supabaseUser.user_metadata?.last_name || '',
        });
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
